import { evaluate, parse } from 'mathjs';
const DEG = Math.PI / 180;
/**
 * The ONE canonical teacher-friendly mathjs override scope for every formula input in the
 * app — originally lived duplicated in two places (the teacher-interface app's own
 * formula-eval.ts, and a "bit-for-bit port" of it inside numerical.ts for Math Error candidate
 * generation), which meant a formula's live "= result" preview and its actual grading-time
 * evaluation could silently disagree (e.g. a formula using `arcsin` would preview fine but fail
 * to parse wherever the un-ported raw-mathjs path evaluated the same string — exactly the bug
 * class that motivated unifying this into a single shared function). Both numerical.ts and
 * dropdown.ts's evaluateEquation now import this directly (same package, no app-layer
 * dependency issue); the app-layer formula-eval.ts also imports it instead of keeping its own
 * copy, so every formula input anywhere in the app evaluates identically.
 *
 * log(x) means base-10 (calculator convention) unless a base is given; ln(x) is natural log;
 * trig functions (and their arcXxx/aXxx inverses) convert through degrees when angleMode is
 * 'degrees'. Variable values are merged in by callers (see buildScopeWithValues) so they take
 * precedence over these function names — an unlikely collision, but the correct priority order.
 */
export function buildFormulaScope(angleMode) {
    const toRad = angleMode === 'degrees' ? (x) => x * DEG : (x) => x;
    const fromRad = angleMode === 'degrees' ? (x) => x / DEG : (x) => x;
    return {
        log: (x, base) => base !== undefined ? Math.log(x) / Math.log(base) : Math.log10(x),
        ln: (x) => Math.log(x),
        sin: (x) => Math.sin(toRad(x)), cos: (x) => Math.cos(toRad(x)), tan: (x) => Math.tan(toRad(x)),
        sec: (x) => 1 / Math.cos(toRad(x)), csc: (x) => 1 / Math.sin(toRad(x)), cot: (x) => 1 / Math.tan(toRad(x)),
        asin: (x) => fromRad(Math.asin(x)), acos: (x) => fromRad(Math.acos(x)), atan: (x) => fromRad(Math.atan(x)),
        atan2: (y, x) => fromRad(Math.atan2(y, x)),
        asec: (x) => fromRad(Math.acos(1 / x)), acsc: (x) => fromRad(Math.asin(1 / x)), acot: (x) => fromRad(Math.atan(1 / x)),
        arcsin: (x) => fromRad(Math.asin(x)), arccos: (x) => fromRad(Math.acos(x)), arctan: (x) => fromRad(Math.atan(x)),
        arcsec: (x) => fromRad(Math.acos(1 / x)), arccsc: (x) => fromRad(Math.asin(1 / x)), arccot: (x) => fromRad(Math.atan(1 / x)),
        root: (x, n) => Math.pow(x, 1 / n), Pi: Math.PI, PI: Math.PI,
    };
}
/** Every angle-dependent function name buildFormulaScope overrides — the trig-detection
 * predicate every "should I show the degrees/radians toggle" UI check is built from, so that
 * list and this one can never drift apart. */
export const ANGLE_FUNCTION_NAMES = new Set([
    'sin', 'cos', 'tan', 'asin', 'acos', 'atan', 'atan2', 'sec', 'csc', 'cot', 'asec', 'acsc', 'acot',
    'arcsin', 'arccos', 'arctan', 'arcsec', 'arccsc', 'arccot',
]);
/** Matches any angle-dependent function's opening call — `hasTrig`-style detection used to
 * decide whether a block's degrees/radians toggle should even be shown. Excludes the `h` suffix
 * (sinh/cosh/tanh are hyperbolic, not angle-dependent) via the trailing negative lookahead. */
export const TRIG_CALL_PATTERN = /\b(?:arc)?(?:a)?(?:sin|cos|tan|sec|csc|cot)(?!h)\s*\(/;
/** Rewrites |expr| → abs(expr) before mathjs sees the string — mathjs has no native |...| syntax.
 * Handles nesting by repeatedly replacing the innermost pair until stable. */
export function preprocessAbsNotation(expr) {
    let prev;
    do {
        prev = expr;
        expr = expr.replace(/\|([^|]+)\|/g, 'abs($1)');
    } while (expr !== prev);
    return expr;
}
/** buildFormulaScope, with resolved variable values merged on top (so a variable can shadow a
 * function name, matching every existing caller's priority order) — the one-call convenience
 * every plain "evaluate this formula against these values" site actually wants. */
export function buildScopeWithValues(angleMode, resolvedVariables) {
    const scope = { ...buildFormulaScope(angleMode) };
    for (const [k, v] of Object.entries(resolvedVariables)) {
        /*
         * BOTH FORMS, so one map serves variables and recalls alike — the same bargain
         * `evaluateFormulaRaw` already struck, moved here so the other evaluator gets it too.
         * A variable reaches scope under its own name; an alias under its sanitised key, which a
         * variable name can never collide with because a variable must start with a letter.
         */
        scope[k] = v;
        scope[formulaRefKey(k)] = v;
    }
    return scope;
}
/** Evaluates a formula to a raw number, NaN on any failure. Variable chips (`{n}`) resolve from
 * `values`; everything else goes through the one shared scope so a formula can never evaluate
 * differently depending on which surface ran it. */
export function evaluateFormulaRaw(expr, values, angleMode = 'radians') {
    const trimmed = expr.trim();
    if (!trimmed)
        return NaN;
    try {
        const scope = { ...buildFormulaScope(angleMode) };
        for (const [k, v] of Object.entries(values)) {
            const n = parseFloat(v);
            if (isNaN(n))
                continue;
            // BOTH forms, so one map serves variables and recalls alike and no caller has to know
            // which kind of thing it is holding. A variable reaches scope under its own name; an
            // alias under its sanitised key, which a variable name can never be.
            scope[k] = n;
            scope[formulaRefKey(k)] = n;
        }
        const resolved = preprocessFormula(trimmed);
        const result = evaluate(resolved, scope);
        return typeof result === 'number' && isFinite(result) ? result : NaN;
    }
    catch {
        return NaN;
    }
}
/**
 * Does this formula PARSE? Not: does it produce a number.
 *
 * `evaluateFormulaRaw` returns NaN for everything that goes wrong, which makes a typo
 * indistinguishable from a dependency that has not resolved yet — and the resolver treats an
 * unresolved dependency as normal, because equations are computed in dependency order and a
 * value may simply not be ready on this pass. So a genuinely broken expression was swallowed and
 * the variable just quietly had no value.
 *
 * Reported from `{star1_lum}/(4*pi*{star1_radius}^2))^0.25`: one closing parenthesis too many.
 * mathjs rejects it outright, but nothing asked mathjs until evaluation time, by which point the
 * failure had been folded in with every other reason a number might be missing.
 *
 * Parsing is the right question because it needs NO scope: whether the brackets balance does not
 * depend on what anything is worth, so this can be asked of a half-built formula whose
 * dependencies are still unresolved.
 */
/**
 * A RECALLED ANSWER'S SCOPE KEY, which cannot be the bare id the way a variable's is.
 *
 * `{name}` becomes the symbol `name`, because a variable's name is already a legal identifier —
 * the panel enforces `/^[a-zA-Z][a-zA-Z0-9_]*$/`. An alias id is `ara_${uuidv4()}` and carries
 * HYPHENS, so `[ara_279668a3-4f1a]` would parse as a subtraction between two unknown symbols and
 * quietly evaluate to NaN. It needs a sanitised symbol instead.
 *
 * The leading underscore is what makes it collision-proof rather than merely unlikely: a variable
 * name must START WITH A LETTER, so no `{name}` substitution can ever produce one of these, and
 * registering both forms in scope is safe.
 */
export function formulaRefKey(aliasId) {
    return `_ref_${aliasId.replace(/[^0-9a-zA-Z]/g, '')}`;
}
/**
 * The rewrites that turn an AUTHORED formula into one mathjs can parse, in one place.
 *
 * Evaluation and the parse check have to agree about what the teacher wrote — `formulaParses`
 * already said so in a comment and then restated the rewrites by hand, which is exactly how the
 * two come apart. A recall token is the third rewrite and the one that would have broken it:
 * `formulaParses('[ara_x]')` was false, so every formula holding a recall read as a syntax error.
 *
 * `[1,2,3]` is caught by this too, and that is the intended reading — a bracket means a recall in
 * this app, and mathjs matrices evaluate to nothing here anyway.
 */
/**
 * A STAND-IN NUMBER FOR EVERY RECALL A FORMULA MENTIONS, for the validation probe.
 *
 * The probe answers one question — is this expression arithmetic at all — and deliberately not
 * what it comes out as; `probeRoll`'s own comment says so. A recalled answer resolves to a
 * number at runtime, so for that question any finite number will do, and 1 is the one that
 * cannot itself cause a fault: not 0, which would make `x/[ref]` look like a division by zero
 * that only the probe invented.
 *
 * Without this a perfectly good `2*[ara_...]` failed validation as "not a usable number", which
 * is the block accusing the teacher of a mistake the block is making.
 *
 * It does NOT check the alias exists. A recall pointing at a deleted answer is a real fault and
 * already has its own report (`brokenAliasUsages`); saying it twice, once in the wrong words,
 * helps nobody.
 */
export function probeValuesForRecalls(expr) {
    const out = {};
    for (const m of expr.matchAll(/\[(ara_[0-9a-zA-Z-]+)\]/g))
        out[m[1]] = '1';
    return out;
}
export function preprocessFormula(expr) {
    return preprocessAbsNotation(expr.replace(/\{([^}]+)\}/g, '$1').replace(/\[([^\]]+)\]/g, (_m, id) => formulaRefKey(id)));
}
export function formulaParses(expr) {
    const trimmed = expr.trim();
    if (!trimmed)
        return true;
    try {
        // Through `preprocessFormula`, which evaluation also uses — the two cannot disagree about
        // what the teacher wrote because there is only one rewrite now, not two copies of it.
        parse(preprocessFormula(trimmed));
        return true;
    }
    catch {
        return false;
    }
}
/**
 * THE ANGLE MODE A PARTICULAR FIELD USES, read the same way everywhere.
 *
 * The authoring side writes `angleModes[fieldId]`; every evaluator has to read it back through
 * THIS, with the same `fieldId` the field was tagged with. Nothing warns when those two drift —
 * the formula simply evaluates in the other mode and produces a plausible wrong number — so the
 * ids are asserted against each other in `angle-mode-field-ids.test.ts`.
 *
 * `angleMode` is the block's old block-wide value, seeding fields never set individually.
 */
export function angleModeFor(data, fieldId) {
    return data?.angleModes?.[fieldId] ?? data?.angleMode ?? 'degrees';
}
//# sourceMappingURL=formula-scope.js.map